import { makeStyles } from "tss-react/mui";
import { MiButton, MiChip, MiIcon, MiIconButton } from "@miplatform/components";
import { BUTTON_VARIANTS } from "@miplatform/constants";
import { ICategoriesResponse, IFloorResponse } from "@miplatform/types";
import { mdiChevronDown, mdiMinusCircle } from "@mdi/js";
import { Accordion, AccordionSummary, Box, Typography } from "@mui/material";
import { ICategoryFloorDropTypesPayload } from "../CustomerPricingDetails/CustomerPricingDetails.types";

export function CategoryFloorsAccordion({
  category,
  availableFloors = [],
  appliedDropTypes = [],
  disabled,
  readOnly = false,
  handleOpenDropTypeSelect = () => {},
  handleRemoveDropType = () => {},
}: Readonly<CategoryFloorsAccordionProps>) {
  const { classes, theme } = useStyles();

  return (
    <Accordion
      key={category.id}
      defaultExpanded
      disableGutters
      className={classes.accordion}
    >
      <AccordionSummary
        expandIcon={<MiIcon path={mdiChevronDown} />}
        className={classes.accordionSummary}
      >
        <Box className={classes.categoryHeader}>
          <Typography className={classes.categoryTitle}>
            {category.name}
          </Typography>
          {category.dropRequired && (
            <MiChip
              label="Requires Drop Per Floor"
              size="small"
              className={classes.dropChip}
            />
          )}
        </Box>
      </AccordionSummary>
      <Box className={classes.floorsContainer}>
        {availableFloors.map((floor) => {
          const floorDropTypes = appliedDropTypes.filter(
            (dropType) => dropType.floorId === floor.id
          );
          return (
            <Box key={floor.id + category.id} className={classes.floorGroup}>
              <Box className={classes.floorHeader}>
                <Typography variant="HD3">{floor.name}</Typography>
                <MiButton
                  title="Select Drop Types"
                  variant={BUTTON_VARIANTS.OUTLINED}
                  startIcon={{ path: mdiChevronDown }}
                  size="small"
                  disabled={disabled || readOnly}
                  onClick={(e) =>
                    // sets selected category and floor ids to determine which drop types to show in the select
                    handleOpenDropTypeSelect(e, category.id, floor.id)
                  }
                />
              </Box>
              {floorDropTypes.map((dropType) => (
                <Box key={dropType.dropTypeId} className={classes.dropTypeCard}>
                  <Typography className={classes.dropTypeName}>
                    {dropType.dropTypeName}
                  </Typography>
                  <MiIconButton
                    disabled={readOnly}
                    onClick={() =>
                      handleRemoveDropType(
                        category.id,
                        floor.id,
                        dropType.dropTypeId
                      )
                    }
                  >
                    <MiIcon
                      path={mdiMinusCircle}
                      color={theme.palette.error.main}
                    />
                  </MiIconButton>
                </Box>
              ))}
            </Box>
          );
        })}
      </Box>
    </Accordion>
  );
}

const useStyles = makeStyles()((theme) => ({
  accordion: {
    marginBottom: theme.spacing(2),
    border: `1px solid ${theme.palette.grey.lightAccent}`,
    backgroundColor: theme.palette.grey.light,
    borderRadius: 2,
    boxShadow: "none",
  },
  accordionSummary: {
    minHeight: 0,
    padding: theme.spacing(0.5, 2),
    backgroundColor: theme.palette.grey.mediumLight,
    "& .MuiAccordionSummary-content": {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      margin: 0,
    },
    "& .MuiAccordionSummary-content.Mui-expanded": {
      margin: 0,
    },
    "&.Mui-expanded": {
      borderBottom: `1px solid ${theme.palette.grey.lightAccent}`,
      minHeight: 0,
    },
  },
  categoryHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
  },
  miBadge: {
    width: "20px !important",
    height: 20,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  categoryTitle: {
    fontWeight: theme.fontWeight.semiBold,
    fontSize: theme.fontSize.medium,
  },
  dropChip: {
    border: `1px solid ${theme.palette.orange.main}`,
    backgroundColor: theme.palette.orange.light,
    borderRadius: 4,
    height: 20,
    color: theme.palette.orange.main,
  },
  floorsContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: theme.spacing(2),
    padding: theme.spacing(2),
  },
  floorGroup: {
    width: "100%",
    borderRadius: 4,
    overflow: "hidden",
    border: `1px solid ${theme.palette.grey.lightAccent}`,
  },
  floorHeader: {
    padding: theme.spacing(2, 3),
    backgroundColor: theme.palette.primary.light,
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  dropTypeCard: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(1.5, 3),
    backgroundColor: theme.palette.common.white,
    borderTop: `1px solid ${theme.palette.grey.lightAccent}`,
  },
  dropTypeName: {
    color: theme.palette.primary.main,
    fontWeight: theme.fontWeight.semiBold,
    fontSize: theme.fontSize.medium,
  },
}));

interface CategoryFloorsAccordionProps {
  readOnly?: boolean;
  category: ICategoriesResponse;
  availableFloors: IFloorResponse[];
  appliedDropTypes?: ICategoryFloorDropTypesPayload[];
  disabled: boolean;
  handleOpenDropTypeSelect: (
    event: React.MouseEvent<HTMLElement>,
    categoryId: string,
    floorId: string
  ) => void;
  handleRemoveDropType?: (
    categoryId: string,
    floorId: string,
    dropTypeId: string
  ) => void;
}
